/**
 * 악덕 케이스 통합 모달 — 카드형 레이아웃.
 *
 *   // 페이지 1회 설정(라벨/목록 새로고침 콜백)
 *   $j.F.Admin.Devil.Case.config({ labels: {master, report, defense}, onChanged: reloadTable });
 *   // 마스터에서 진입 (상단 요약카드 + 신고목록, 최신 신고 자동선택)
 *   $j.F.Admin.Devil.Case.openMaster(masterNum);
 *   // 신고에서 진입 (신고 상세 먼저 → master_num 있으면 상단 요약/목록 자동 로드)
 *   $j.F.Admin.Devil.Case.openReport(reportNum);
 *
 * 레이아웃:
 *   ┌ 상단 요약카드(핑크) : 업체 요약 + 기본정보 + 변경내역
 *   └ 하단 카드(파랑)      : [신고건 목록] │ [신고 상세(스텝퍼·신고자/체불업체소명·변경이력)]
 *
 * 상단/목록은 $j.F.Admin.Devil.Master, 상세는 $j.F.Admin.Devil.Report 를
 * host 모드로 재사용한다 — 백엔드/액션 로직 변경 없음.
 */
import LayoutData from '../LayoutData';

export default class Case {
    /** 원장에서 이 종류를 부르는 이름 — TaskGuard 가 쓴다 */
    static TASK_TYPE = 'devil';

    constructor() {
        this.modalId   = 'devil-case';
        // 라벨(상태 표기)은 원래 화면이 넘겨주는 값이었는데, 알림 모아보기처럼 그 전역이 없는
        // 화면에서도 열리게 되면서 레이아웃이 넘긴 것을 기본으로 쓴다(F/Admin/LayoutData 'dvStatus').
        // config({labels}) 를 주면 그쪽이 이긴다 — 정본은 G5_DV_*_STATUS (app/Common/constants.php)
        this.labels    = Object.assign({ master: {}, report: {}, defense: {} }, LayoutData.get('dvStatus', {}));
        this.pageOnChanged = null; // 목록 페이지 새로고침
        this.masterNum = 0;
        this.reportNum = 0;
        this._noMaster = false;    // 승인 전 신고(마스터 미채택) — 상단카드/목록 숨김
        this._master   = null;
        this._reports  = [];
        this._styleInjected = false;

        // 상단 요약카드 + 신고목록 host — Master 모듈이 여기에 렌더한다.
        this._masterHost = {
            mountSummary: (html, m) => {
                $(this._sel('.dvc2-top-body')).html(html);
                this._master = m; this._setNoMaster(false); this._title();
            },
            mountList: (html, reports) => {
                $(this._sel('.dvc2-list-body')).html(html);
                this._reports = reports || [];
                if (!this.reportNum && this._reports.length) this._selectReporter(this._reports[0].num);
                else this._highlightRow(this.reportNum);
            },
            onSelectReport: (n) => this._selectReporter(n),
        };
        // 신고 상세 host — Report 모듈이 여기에 렌더한다.
        this._reportHost = {
            mountReport:   (html, num, r) => { $(this._sel('.dvc2-detail-body')).html(html); this._onReportLoaded(r); this._title(); },
            masterVisible: () => !this._noMaster,
            onShowMaster:  (m) => { this.masterNum = m; this._setNoMaster(false); this._renderMaster(); },
        };
    }

    config = (opts = {}) => {
        if (opts.labels) this.labels = Object.assign({}, this.labels, opts.labels);
        if (opts.onChanged !== undefined) this.pageOnChanged = opts.onChanged;
        return this;
    }

    // ── 진입점 ───────────────────────────────────────────────
    /**
     * 업체(마스터)로 연다 — 상단 요약카드 + 신고목록, 최신 신고가 자동으로 골라진다.
     *
     * 원장은 **신고 한 건** 단위라 여기서는 아직 볼 것이 없다. 겹침 확인은 신고를
     * 고르는 순간(_selectReporter)에 한다 — 자동선택도 그 길을 지난다.
     */
    openMaster = (num, opts = {}) => {
        this._applyOpts(opts);
        this.masterNum = num; this.reportNum = 0;
        this._noMaster = false; this._master = null; this._reports = [];
        this._ensure();
        this._renderMaster();
    }

    /**
     * 신고 한 건으로 연다.
     *
     * **열기 전에 원장을 본다** — 담당·처리 상태는 거기에만 있어서, 남이 붙잡고 있는 건을
     * 모르고 겹쳐 심사하는 것을 막는다. 판단은 공용($j.F.Admin.TaskGuard)이라 짜고치기
     * 판정창과 같은 문구·같은 규칙이다. 아무도 안 맡았으면 여는 사람이 담당이 된다.
     */
    openReport = async (num, opts = {}) => {
        if (!await $j.F.Admin.TaskGuard.check(Case.TASK_TYPE, num,
                { onClaimed: () => { if (this.pageOnChanged) this.pageOnChanged(); } })) return;

        this._applyOpts(opts);
        this.reportNum = num; this.masterNum = 0;
        this._master = null; this._reports = [];
        this._noMaster = true;    // _ensure 초기 폭을 신고 단독(좁게)으로 — 넓게 떴다 줄어드는 깜빡임 방지
        this._ensure();
        this._setNoMaster(true);  // 마스터 확인 전까지 상단카드/목록 숨김(빈카드 깜빡임 방지)
        this._renderReport();     // 마스터는 신고 로드 후 master_num 있으면 자동 로드
        this._badge(num);         // 머리줄 공통 칩 — 이 길(신고에서 바로 열기)이 가장 흔하다
    }

    // ── 상태 전이 ────────────────────────────────────────────
    /**
     * 신고 목록에서 한 건을 고른다 — 마스터로 열었을 때 자동으로 고르는 최신 건도 여기를 지난다.
     *
     * **여기서도 원장을 본다.** 진입점이 마스터든 신고든 보는 것은 결국 신고 한 건이라,
     * 한쪽만 물으면 마스터로 들어온 사람은 남이 붙잡은 건을 모르고 겹쳐 심사한다.
     * 이미 열려 있는 건을 다시 고른 것이면 묻지 않는다.
     */
    _selectReporter = async (num) => {
        if (num !== this.reportNum && !await $j.F.Admin.TaskGuard.check(Case.TASK_TYPE, num,
                { onClaimed: () => { if (this.pageOnChanged) this.pageOnChanged(); } })) return;

        this.reportNum = num;
        this._highlightRow(num);
        this._renderReport();
        this._badge(num);
    }

    /**
     * 머리줄의 공통 칩 — 담당·상태·이력.
     * 이 창은 **한 번 열고 신고를 옮겨 다닌다**(업체 카드의 신고목록). 그래서 창을 열 때가
     * 아니라 **신고를 고를 때마다** 다시 그린다 — 안 그러면 옆 신고의 담당이 그대로 남는다.
     */
    _badge = (num) => {
        $j.F.Admin.TaskBadge.attach($('#' + this.modalId), Case.TASK_TYPE, num,
            { onChanged: () => { if (this.pageOnChanged) this.pageOnChanged(); } });
    }

    // 신고 상세가 로드되면 — 채택된 마스터가 있고 아직 안 실렸으면 상단카드/목록 로드.
    _onReportLoaded = (r) => {
        const mnum = Number(r && r.master_num) || 0;
        if (mnum && this.masterNum !== mnum) {
            this.masterNum = mnum;
            this.reportNum = r.num;   // mountList 자동선택이 이 신고을 덮지 않도록 먼저 고정
            this._renderMaster();
        } else if (!mnum && !this.masterNum) {
            this._setNoMaster(true);  // 승인 전 신고 — 마스터 없음(상세만)
        }
        if (mnum) this._highlightRow(r.num);
    }

    // 액션(승인/반려/회수/해제 등) 후 동기화 — 상단카드/목록 갱신 + 목록 페이지 새로고침.
    _afterAction = () => {
        if (this.pageOnChanged) this.pageOnChanged();
        if (this.masterNum) this._renderMaster();
        if (this.reportNum) this._badge(this.reportNum);   // 정정요청을 보내면 서버가 보낸 사람을 담당으로 잡는다 — 칩을 다시 읽는다
    }

    // ── 렌더 ─────────────────────────────────────────────────
    _renderMaster = () => {
        $j.F.Admin.Devil.Master.open(this.masterNum, {
            host: this._masterHost, selected: this.reportNum,
            labels: this.labels, onChanged: this._afterAction,
        });
    }
    _renderReport = () => {
        if (!this.reportNum) return;
        $j.F.Admin.Devil.Report.open(this.reportNum, {
            readOnly: false, host: this._reportHost,
            labels: this.labels, onChanged: this._afterAction,
        });
    }

    _highlightRow = (num) => {
        const $l = $(this._sel('.dvc2-list-body'));
        $l.find('.dvc2-rep').removeClass('is-selected');
        $l.find('.dvc2-rep[data-rep="' + num + '"]').addClass('is-selected');
    }

    // ── 모달/레이아웃 ────────────────────────────────────────
    _ensure = () => {
        this._injectStyle();
        const skeleton = `
            <div class="dvc2" data-nomaster="0">
                <div class="dvc2-topcard"><div class="dvc2-top-body"></div></div>
                <div class="dvc2-bottomcard">
                    <div class="dvc2-list"><div class="dvc2-list-body"></div></div>
                    <div class="dvc2-detail"><div class="dvc2-detail-body">
                        <div class="dvc2-empty">제보건을 선택하세요.</div>
                    </div></div>
                </div>
            </div>`;
        $j.Ui.JBModal.html(this.modalId, skeleton, '', {});
        this._applySize();
    }

    // 모달 크기 — 신고 단독(마스터 미채택)이면 상세폭에 맞춰 좁게, 케이스(마스터+목록)면 넓게.
    //
    //  · 가로: 신고 단독 1000px → 상세 content ≈ 932px 로 케이스모달 안 detail 패널과 동일
    //  · 세로: **양쪽 다 height:auto** — 내용에 맞춰 줄고, 넘치면 maxHeight 안에서 안쪽 패널이 스크롤한다.
    //
    //    케이스 쪽은 예전에 `height: calc(100vh - 40px)` 로 못박혀 있었다. 신고가 한 건인
    //    업체(대부분이다)를 열면 아래가 300px 가까이 비었다. 높이를 고정한 이유는 안쪽
    //    스크롤(`.dvc2-rep-list`·`.dvc2-detail` 의 overflow-y:auto)에 확정 높이가 필요해서인데,
    //    그건 max-height 로도 성립한다 — 조상들이 `min-height:0` 을 갖고 있어서 maxHeight 에
    //    걸리는 순간 플렉스가 눌리고 그때부터 안쪽이 스크롤한다.
    _applySize = () => {
        const $m = $('#' + this.modalId);
        // vw/vh 는 --adm-zoom 으로 나눈다 — 글자크기 3단계가 body 에 zoom 을 걸면
        // (admin/base/_fontsize.scss) 뷰포트 단위는 확대 **전** 값으로 풀린 뒤 렌더링에서
        // 다시 배율만큼 커져 모달이 화면 밖으로 넘친다. px 값은 나누지 않는다 — 그쪽은
        // 같이 커지는 게 맞다. 폴백 1 이라 보통(m)에서는 값이 그대로다.
        // ⚠ `var(--adm-zoom, 1)` 을 변수로 빼지 않는다 — /adm/theme 의 뷰포트단위 점검이
        //   **줄 단위**로 표식을 찾기 때문에, 변수로 우회하면 고쳐 놓고도 미보정으로 잡힌다.
        const w = this._noMaster
            ? 'min(1000px, calc(94vw / var(--adm-zoom, 1)))'
            : 'min(1300px, calc(96vw / var(--adm-zoom, 1)))';
        $m.css({
            width: w, minWidth: w, maxWidth: 'calc(96vw / var(--adm-zoom, 1))',
            height: 'auto', maxHeight: 'calc((100vh - 40px) / var(--adm-zoom, 1))',
        });
    }

    _setNoMaster = (v) => {
        this._noMaster = !!v;
        $(this._sel('.dvc2')).attr('data-nomaster', v ? '1' : '0');
        this._applySize();
        this._title();
    }

    _title = () => {
        const $t = $('#' + this.modalId + ' .jb-modal-title');
        if (this._noMaster || !this.masterNum) { $t.text('악덕제보 #' + this.reportNum); return; }
        const cnt = (this._master && this._master.report_cnt) || this._reports.length || 0;
        $t.html('악덕업체 <span class="dvc2-title-pill">#' + this.masterNum + ' · 제보 ' + cnt + '건</span>');
    }

    _applyOpts = (opts) => {
        if (opts.labels) this.labels = Object.assign({}, this.labels, opts.labels);
        if (opts.onChanged !== undefined) this.pageOnChanged = opts.onChanged;
    }
    _sel = (sub) => '#' + this.modalId + ' ' + sub;

    _injectStyle = () => {
        if (this._styleInjected) return;
        this._styleInjected = true;
        const css = `
/* ⚑ 이 CSS 는 런타임에 head 로 주입되고, 선택자가 모달 ID 로 시작한다.
   특이도가 (1,1,0) 이라 adm.css 의 토큰 규칙(0,2,0)을 전부 이긴다 —
   그래서 값을 여기서 토큰으로 받아야 화면설정 테마(html[data-ui])를 탄다.
   반경은 --adm-radius-*, 그림자는 --adm-shadow-panel, 모달 본문 여백은 --ui-modal-pad.
   남긴 것은 모양 그 자체다 — 50%(원형 번호), 999px(알약 뱃지).
   주의: 이 안은 템플릿 리터럴이라 백틱을 쓰면 문자열이 끊긴다. */

#${this.modalId} .jb-modal-body{display:flex;flex-direction:column;overflow:hidden;padding:var(--ui-modal-pad);background:var(--adm-b-f7f8fa, #f7f8fa)}
#${this.modalId} .jb-modal-title{display:flex;align-items:center;gap:8px}
.dvc2-title-pill{background:var(--adm-b-ffe3ec, #ffe3ec);color:var(--adm-c-d6336c, #d6336c);font-size:13px;font-weight:700;border-radius:var(--adm-radius-md);padding:2px 10px}
.dvc2{display:flex;flex-direction:column;gap:12px;flex:1;min-height:0}

/* 상단 요약카드(핑크) — 컴팩트, 고정 높이 */
.dvc2-topcard{flex:none;border:1.5px solid #f2a9c4;border-radius:var(--adm-radius-md);background:var(--adm-b-ffffff, #fff);padding:13px 16px;box-shadow:var(--adm-shadow-panel)}
.dvc2-top-head{display:flex;align-items:center;gap:10px;flex-wrap:wrap;margin-bottom:11px}
.dvc2-tag{background:var(--adm-b-fde7ef, #fde7ef);color:var(--adm-c-c2185b, #c2185b);font-size:12px;font-weight:700;border-radius:var(--adm-radius-md);padding:3px 10px}
.dvc2-co{font-size:18px;font-weight:800;color:var(--adm-c-212529, #212529)}
.dvc2-spacer{flex:1}
/* 체불 해제/복구 버튼 — 참고파일 색상: 해제=다크, 해제됨=흰바탕·빨강 */
.dvc2-release{background:#121212;color:#fff;border:none;border-radius:var(--adm-radius-sm);height:38px;padding:0 18px;font-size:14px;font-weight:700;cursor:pointer}
.dvc2-release:hover{background:#000}
.dvc2-release.released{background:var(--adm-b-ffffff, #fff);color:var(--adm-c-ff382d, #FF382D);border:1px solid #FF382D}
.dvc2-release.released:hover{background:var(--adm-b-fff5f5, #fff5f5)}
.dvc2-logbtn{display:inline-flex;align-items:center;gap:6px;height:38px;background:var(--adm-b-ffffff, #fff);color:var(--adm-c-495057, #495057);border:1px solid #ced4da;border-radius:var(--adm-radius-sm);padding:0 15px;font-size:14px;font-weight:700;cursor:pointer}
.dvc2-logbtn.open{border-color:#f06595;color:var(--adm-c-d6336c, #d6336c);background:var(--adm-b-fff0f6, #fff0f6)}
.dvc2-logcount{font-size:12px;font-weight:700;color:#fff;background:#adb5bd;border-radius:var(--adm-radius-md);padding:1px 7px}
.dvc2-logbtn.open .dvc2-logcount{background:#f06595}
.dvc2-phone{display:inline-block;color:#fff;font-weight:600;border-radius:var(--adm-radius-md);padding:2px 9px;margin:1px 3px 1px 0;font-size:13px;white-space:nowrap}

/* 기본정보 — 참고파일: 연락처 강조 · 체불금액 큰 숫자 · 인라인 메타 */
.dvc2-info{display:flex;flex-direction:column;gap:9px}
.dvc2-info-lbl{flex:none;font-size:13px;font-weight:600;color:var(--adm-c-909090, #909090)}
.dvc2-phones-row{display:flex;align-items:center;gap:12px;flex-wrap:wrap}
.dvc2-phones{display:flex;align-items:center;gap:8px;flex-wrap:wrap}
.dvc2-phones-row .dvc2-phone{font-size:14px;padding:5px 13px;margin:0}
.dvc2-meta-row{display:flex;align-items:center;gap:12px 24px;flex-wrap:wrap}
.dvc2-total{display:flex;align-items:baseline;gap:8px}
.dvc2-total b{font-size:20px;font-weight:800;letter-spacing:-.5px;color:var(--adm-c-212529, #212529)}
.dvc2-meta{display:flex;flex-wrap:wrap;gap:6px 18px;font-size:14px;color:var(--adm-c-212529, #212529)}
.dvc2-meta i{font-style:normal;color:var(--adm-c-909090, #909090);margin-right:5px}
.dvc2-logpanel{margin-top:12px;max-height:180px;overflow-y:auto;padding-right:6px;border-top:1px solid #f1f3f5}
.dvc2-logitem{display:flex;align-items:center;gap:10px;font-size:13px;padding:6px 2px;border-bottom:1px dashed #edf0f2}
.dvc2-logitem:last-child{border-bottom:none}
.dvc2-logt{flex:none;width:126px;color:var(--adm-c-868e96, #868e96);white-space:nowrap}
.dvc2-logm{flex:1;color:var(--adm-c-343a40, #343a40);min-width:0}
.dvc2-logm b{color:var(--adm-c-e64980, #e64980)}
.dvc2-logby{flex:none;font-size:12px;font-weight:600;color:var(--adm-c-495057, #495057);background:var(--adm-b-f1f3f5, #f1f3f5);border-radius:var(--adm-radius-md);padding:2px 9px;white-space:nowrap}
.dvc2-logempty{color:#adb5bd;font-size:13px;padding:12px 2px}

/* 하단 카드(파랑) — 남은 높이 채움 */
.dvc2-bottomcard{flex:1;min-height:0;display:flex;border:1.5px solid #a5c8f5;border-radius:var(--adm-radius-md);background:var(--adm-b-ffffff, #fff);overflow:hidden;box-shadow:var(--adm-shadow-panel)}
.dvc2-list{flex:0 0 300px;display:flex;flex-direction:column;min-height:0;border-right:1px solid #eef1f4;padding:12px 10px;background:var(--adm-b-fcfdff, #fcfdff)}
.dvc2-detail{flex:1 1 auto;min-width:0;min-height:0;overflow-y:auto;padding:14px}
.dvc2[data-nomaster="1"] .dvc2-topcard,.dvc2[data-nomaster="1"] .dvc2-list{display:none}
/* 목록 칼럼의 세로 사슬 — .dvc2-list(flex column) → 여기 → .dvc2-rep-list(flex:1;overflow:auto).
   이 한 줄이 없으면 사슬이 여기서 끊겨 rep-list 의 flex:1 이 안 먹고 내용만큼 늘어난다.
   화면이 낮아 모달이 max-height 에 걸릴 때 목록이 스크롤되지 않고 잘려 보였다. */
.dvc2-list-body{flex:1;min-height:0;display:flex;flex-direction:column}
.dvc2-list-head{margin-bottom:12px}
.dvc2-list-badge{display:inline-flex;align-items:center;gap:3px;background:var(--adm-b-e9f0fe, #e9f0fe);color:var(--adm-c-3e76ed, #3e76ed);font-size:13px;font-weight:600;padding:4px 11px;border-radius:var(--adm-radius-chip, 999px)}
.dvc2-list-badge b{font-weight:700}
.dvc2-search{position:relative;margin-bottom:10px}
.dvc2-search i{position:absolute;left:11px;top:50%;transform:translateY(-50%);color:#adb5bd;font-size:13px}
.dvc2-search input{width:100%;border:1px solid #e3e8ef;border-radius:var(--adm-radius-sm);padding:8px 10px 8px 30px;font-size:14px;background:var(--adm-b-f8fafc, #f8fafc)}
.dvc2-search input:focus{outline:none;border-color:#748ffc;background:var(--adm-b-ffffff, #fff)}
.dvc2-rep-list{flex:1;min-height:0;overflow-y:auto;margin:0 -4px;padding:0 4px}
.dvc2-rep{display:flex;align-items:center;gap:10px;padding:8px 8px;border-radius:var(--adm-radius-sm);cursor:pointer;border:1px solid transparent}
.dvc2-rep+.dvc2-rep{margin-top:3px}
.dvc2-rep:hover{background:var(--adm-b-f4f6f9, #f4f6f9)}
.dvc2-rep.is-selected{background:var(--adm-b-eaf2ff, #eaf2ff);border-color:#a5c8f5}
.dvc2-rep-no{flex:0 0 auto;width:28px;height:28px;border-radius:50%;background:var(--adm-b-e9ecef, #e9ecef);color:var(--adm-c-495057, #495057);font-size:14px;font-weight:700;display:flex;align-items:center;justify-content:center}
.dvc2-rep.is-selected .dvc2-rep-no{background:#4285f4;color:#fff}
.dvc2-rep-main{flex:1 1 auto;min-width:0}
.dvc2-rep-name{font-size:14px;font-weight:700;color:var(--adm-c-212529, #212529)}
.dvc2-rep-sub{font-size:13px;color:var(--adm-c-495057, #495057);margin-top:2px}
.dvc2-rep-date{color:#adb5bd;font-size:12px}
.dvc2-rbadge{display:inline-block;font-size:11px;font-weight:700;border-radius:var(--adm-radius-xs);padding:1px 6px;margin:0 2px}
.dvc2-rbadge-APPROVED{background:var(--adm-b-d3f9d8, #d3f9d8);color:var(--adm-c-2b8a3e, #2b8a3e)}
.dvc2-rbadge-SUBMITTED{background:var(--adm-b-fff3bf, #fff3bf);color:#e67700}
.dvc2-rbadge-DEFENDING,.dvc2-rbadge-NOTIFIED{background:var(--adm-b-d0ebff, #d0ebff);color:var(--adm-c-1971c2, #1971c2)}
.dvc2-rbadge-REJECTED{background:var(--adm-b-ffe3e3, #ffe3e3);color:var(--adm-c-c92a2a, #c92a2a)}
.dvc2-rbadge-RECALLED,.dvc2-rbadge-EXPIRED,.dvc2-rbadge-DRAFT{background:var(--adm-b-e9ecef, #e9ecef);color:var(--adm-c-495057, #495057)}
.dvc2-rbadge-REC{background:var(--adm-b-d0ebff, #d0ebff);color:var(--adm-c-1565c0, #1565c0)}
.dvc2-empty{color:#adb5bd;font-size:14px;text-align:center;padding:60px 0}

/* 마스터 상태 배지(REG/RELEASED) — reports 페이지에도 자립 노출 */
#${this.modalId} .badge-status{font-size:11px;padding:3px 7px;border-radius:var(--adm-radius-xs);font-weight:700}
#${this.modalId} .badge-REG{background:var(--adm-b-ffe3ec, #ffe3ec);color:var(--adm-c-c2185b, #c2185b)}
#${this.modalId} .badge-RELEASED{background:var(--adm-b-e9ecef, #e9ecef);color:var(--adm-c-495057, #495057)}

@media(max-width:900px){
  .dvc2-bottomcard{flex-direction:column}
  .dvc2-list{flex:none;border-right:0;border-bottom:1px solid #eef1f4}
  .dvc2-rep-list{max-height:230px}
}`;
        $('<style>').text(css).appendTo('head');
    }
}
